import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Settings, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEnergy } from '../contexts/EnergyContext';
import BottomNav from './BottomNav';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { totalEnergyPercent } = useEnergy();

  return (
    // min-h-[100dvh] — iOS WebView'da `100vh` URL bar + home indicator alanını
    // yanlış hesaplıyordu (alt boşluk). `dvh` dynamic viewport height,
    // Tailwind 3.4+ native destek.
    <div className="flex flex-col min-h-[100dvh] bg-[#0A0A0F]">
      {/* Header */}
      <header className="sticky top-0 z-50 safe-area-top">
        <div className="flex items-center justify-between px-4 py-3 bg-[#12121A]/80 backdrop-blur-3xl border-b border-[rgba(255,255,255,0.08)]">
          {/* App title */}
          <motion.h1
            className="text-lg font-display"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <span className="text-[#00E5CC] uppercase tracking-[3px]">OMNISIO</span>
            <span className="text-[#94A3B8] text-xs ms-1 tracking-[2.5px] uppercase font-sans">WORKOUT</span>
          </motion.h1>

          {/* Right side: energy + settings */}
          <div className="flex items-center gap-3">
            {/* Energy mini indicator */}
            <div className="flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-[#00E5CC]" />
              <div className="w-20 h-1.5 rounded-full bg-[rgba(255,255,255,0.08)] overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: 'linear-gradient(90deg, #00E5CC, #00B4D8)',
                    boxShadow: '0 0 8px rgba(0, 229, 204, 0.15)',
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${totalEnergyPercent}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
              </div>
              <span className="text-[10px] text-[#00E5CC] font-semibold font-sans">
                {Math.round(totalEnergyPercent)}%
              </span>
            </div>

            {/* Settings */}
            <button
              onClick={() => navigate('/settings')}
              className="p-2.5 rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={t('nav.openSettingsAria', 'Open settings')}
            >
              <Settings className="w-5 h-5 text-[#64748B]" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Main content area
          - Dinamik pb (Layout tek noktadan, sayfalar artık çift-padding yapmaz)
          - max-w-screen-md tablet/iPad'de içeriği makul genişlikte tutar
            (Whoop/Apple Fitness+ pattern — full-width yerine merkezi sütun) */}
      <main
        className="flex-1 overflow-y-auto"
        style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mx-auto w-full max-w-screen-md lg:max-w-screen-lg"
        >
          {children}
        </motion.div>
      </main>

      {/* Bottom navigation */}
      <BottomNav />
    </div>
  );
}
